import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnInit, inject, input, output } from '@angular/core';
import { Router } from '@angular/router';

import { EpcotConfig } from '@frontend/sports/common/client-config-data-access';
import { hasTarget } from '@frontend/sports/common/core/utils/dom';
import { CollapsedState, GridEvent, GridMedia } from '@frontend/sports/grid/core/feature/model';
import { RedirectHelperService } from '@frontend/sports/navigation/core/feature/redirect';
import { RemoveHrefDirective } from '@frontend/sports/navigation/core/feature/remove-href';
import { UrlHelperService } from '@frontend/sports/navigation/core/feature/url-helper';
import { SignpostType, SignpostTypeService } from '@frontend/sports/price-boost/feature';
import { StatisticsConfigService } from '@frontend/sports/statistics/core/feature';
import { TrackingSource } from '@frontend/sports/tracking/feature';
import { DsTouchClickDirective } from '@frontend/ui/shared';
import { DeviceService, NativeAppService } from '@frontend/vanilla/core';
import { SpeculativeLinkDirective } from '@frontend/vanilla/features/speculative-link';
import { IconCustomComponent } from '@frontend/vanilla/shared/icons';

import { GridScoreboardComponent } from '../../grid-scoreboard/components/grid-scoreboard.component';
import { EventDetailComponent } from '../content/event-detail.component';
import { EventFooterComponent } from '../content/event-footer.component';
import { EventInfoComponent } from '../content/event-info.component';
import { EventMediaComponent } from '../content/event-media.component';

@Component({
    selector: 'ms-event-header-scoreboard',
    templateUrl: './event-header-scoreboard.component.html',
    styleUrl: './event-header-scoreboard.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,
        IconCustomComponent,
        EventMediaComponent,
        EventInfoComponent,
        EventDetailComponent,
        GridScoreboardComponent,
        DsTouchClickDirective,
        SpeculativeLinkDirective,
        RemoveHrefDirective,
        EventFooterComponent,
    ],
    providers: [SignpostTypeService],
})
export class EventHeaderScoreboardComponent implements OnInit {
    private readonly router = inject(Router);
    event = input.required<GridEvent>();
    media = input.required<GridMedia>();
    showParticipantImage = input.required<boolean>();
    state = input.required<CollapsedState | undefined>();
    tracking = input.required<TrackingSource>();

    readonly getToggle = output<void>();
    readonly statsClicked = output<void>();

    get eventDetails(): string {
        return this.isExpanded ? this.trackingExpanded : this.trackingCollapsed;
    }

    private readonly trackingCollapsed = 'Collapse';
    private readonly trackingExpanded = 'Expand';

    isExpanded = true;
    eventUrl: string;
    shouldRemoveHref: boolean;
    empty: boolean;
    showParticipantStats: boolean;
    isAllMarketsLinkEnabled: boolean;
    gridEventDisplayOverride: boolean;
    isEpcotEnabled: boolean;

    private readonly urlHelper = inject(UrlHelperService);
    private readonly deviceService = inject(DeviceService);
    private readonly nativeAppService = inject(NativeAppService);
    private readonly redirectHelper = inject(RedirectHelperService);
    private readonly statisticsConfigService = inject(StatisticsConfigService);
    private readonly epcotConfig = inject(EpcotConfig);
    private readonly signpostTypeService = inject(SignpostTypeService);

    ngOnInit(): void {
        this.showParticipantStats = this.statisticsConfigService.showStats(this.event().sport.id);
        this.isEpcotEnabled = this.epcotConfig.isEnabled;
        this.shouldRemoveHref = this.urlHelper.shouldRemoveHref();
        this.signpostTypeService.signpostType = SignpostType.PriceBoost;
        this.eventUrl = this.urlHelper.getEventUrl(this.event());
    }

    toggle(): void {
        this.isExpanded = !this.isExpanded;
        this.getToggle.emit();
    }

    eventClick(event: Event): void {
        if (!this.deviceService.isTouch && !this.nativeAppService.isTerminal) {
            if (hasTarget(event, HTMLAnchorElement)) {
                return;
            }

            this.redirectHelper.goToPage(this.eventUrl);
        }
    }

    navigate(event: Event): void {
        event.preventDefault();
        event.stopPropagation();
        this.router.navigate([this.eventUrl]);
    }
}
